import codes from 'i18n-iso-countries/codes.json';
import Joi from 'joi';
import enLocale from './enLocale.json';

// Build lookup maps directly from raw JSON — bypasses i18n-iso-countries runtime initialization
const alpha2ToAlpha3: Record<string, string> = {};
const alpha3Valid = new Set<string>();
const nameToAlpha3: Record<string, string> = {};

for (const entry of codes as [string, string, ...unknown[]][]) {
  const a2 = entry[0].toUpperCase();
  const a3 = entry[1].toUpperCase();
  alpha2ToAlpha3[a2] = a3;
  alpha3Valid.add(a3);
}

const localeCountries = (enLocale as { locale: string; countries: Record<string, string | string[]> }).countries;
for (const [a2, nameOrNames] of Object.entries(localeCountries)) {
  const a3 = alpha2ToAlpha3[a2.toUpperCase()];
  if (!a3) continue;
  const names = Array.isArray(nameOrNames) ? nameOrNames : [nameOrNames];
  for (const name of names) nameToAlpha3[name.toLowerCase()] = a3;
}

const dictionary: Record<string, string[]> = {
  USA: ['united states'],
  BRA: ['brasil']
};

const INVISIBLE_CHARS_RE = /[\u00AD\u200B-\u200F\u202A-\u202E\u2060-\u2064\uFEFF]/g;

const countryExtension = (joi: Joi.Root): Joi.Extension => ({
  type: 'country',
  base: joi.string(),
  messages: {
    'country.invalid': 'The {{#label}} is not a valid country.'
  },
  validate: (value: string | undefined | null, helpers: Joi.CustomHelpers<string | undefined | null>) => {
    if (value == null || value.trim() === '') return { value: undefined };

    const cleaned = value.replace(INVISIBLE_CHARS_RE, '').trim();
    const upper = cleaned.toUpperCase();
    const lower = cleaned.toLowerCase();

    // Alpha-2 code (e.g. "CA" → "CAN")
    if (alpha2ToAlpha3[upper]) return { value: alpha2ToAlpha3[upper] };

    // Alpha-3 code (e.g. "CAN")
    if (alpha3Valid.has(upper)) return { value: upper };

    // English country name (e.g. "Canada" → "CAN")
    if (nameToAlpha3[lower]) return { value: nameToAlpha3[lower] };

    // Custom dictionary
    for (const [key, possibilities] of Object.entries(dictionary)) if (possibilities.includes(lower)) return { value: key.toUpperCase() };

    return {
      value: upper,
      errors: [helpers.error('country.invalid')]
    };
  }
});

export default countryExtension;
